import React from 'react';
import { Box, Card, CardContent, Typography, useTheme } from '@mui/material';
import HandshakeIcon from '@mui/icons-material/Handshake';

// FEAT-040 — Independent-Connection disclaimer (MVP-8.4, MVP-18.40).
//
// Surfaced in two places (per docs/context/features.yaml FEAT-040):
//   1) caregiver sign-up flow (must-view step, not invisible click-through)
//   2) settings -> "About Careva"
//
// Copy follows §1.2 elderly-friendly tone: warm, plain, no jargon.
// ARIA labelling (§15) is applied so screen readers announce the
// disclaimer as a clearly labelled note.

export const INDEPENDENT_CONNECTION_DISCLAIMER_HEADLINE =
  'Careva is a tool to help people connect independently and is not an employer.';

export const INDEPENDENT_CONNECTION_DISCLAIMER_BODY =
  'You and the people you meet on Careva work out your own arrangements directly. ' +
  'Careva does not hire, manage, or pay caregivers. We are simply here to help warm, ' +
  'trusted connections happen.';

export interface IndependentConnectionDisclaimerProps {
  /** Optional dense layout for compact mounts (e.g., onboarding step). */
  compact?: boolean;
  /** Override the default test id so multiple instances can be located. */
  testId?: string;
}

const IndependentConnectionDisclaimer: React.FC<IndependentConnectionDisclaimerProps> = ({
  compact = false,
  testId = 'independent-connection-disclaimer',
}) => {
  const theme = useTheme();

  return (
    <Card
      role="note"
      aria-labelledby={`${testId}-headline`}
      aria-describedby={`${testId}-body`}
      data-testid={testId}
      sx={{
        backgroundColor: theme.palette.primary.light
          ? theme.palette.action.hover
          : theme.palette.background.paper,
        borderLeft: `4px solid ${theme.palette.primary.main}`,
        borderRadius: 2,
        mb: compact ? 1 : 2,
      }}
    >
      <CardContent sx={{ py: compact ? 1.5 : 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <HandshakeIcon
            color="primary"
            aria-hidden="true"
            sx={{ mt: 0.25, fontSize: compact ? 22 : 28 }}
          />
          <Box>
            <Typography
              id={`${testId}-headline`}
              variant={compact ? 'subtitle1' : 'h6'}
              component="p"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              {INDEPENDENT_CONNECTION_DISCLAIMER_HEADLINE}
            </Typography>
            <Typography
              id={`${testId}-body`}
              variant="body1"
              component="p"
              sx={{ lineHeight: 1.6 }}
            >
              {INDEPENDENT_CONNECTION_DISCLAIMER_BODY}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default IndependentConnectionDisclaimer;
